import { getViemClients, } from "@bracket/contractor-next";
import { assertAllowance,} from "@bracket/contractor-next/erc20";
import {
	getTokenDefinition,
	getContractWithABI,
	isBlockHash,
	isAddress,
} from "@bracket/chainography";

import { parseUnits, } from "viem";

import type { TokenIdentifier, ValidChainId, BlockHash, VaultAddress, } from "@bracket/types";
import type { Config } from "@wagmi/core";



export declare type BracketVaultDepositProps = {
	vault: VaultAddress;
	amount: number | bigint;
	token?: TokenIdentifier;
	chainId?: ValidChainId;
	config?: Config;
	timeout?: number;
};



/**
 * Deposits tokens into a Bracket Vault V2 contract
 * @param {Object} params - The deposit parameters
 * @param {VaultAddress} params.vault - The address of the vault contract
 * @param {number|bigint} params.amount - The amount of tokens to deposit
 * @param {TokenIdentifier} [params.token="BRKTETH"] - The token identifier
 * @param {Config} [params.config] - The wagmi config
 * @param {ValidChainId} [params.chainId=1] - The chain ID
 * @param {number} [params.timeout=30000] - Transaction receipt timeout in ms
 * @returns {Promise<BlockHash>} The transaction hash
 * @throws {Error} If vault address is invalid, approval fails, or transaction fails
 */
export const deposit = async ({
	vault,
	amount,
	token = { symbol: "BRKTETH", chainId: 1, },
	config,
	chainId = 1,
	timeout = 30000,
}: BracketVaultDepositProps): Promise<BlockHash> => {

	try {

		if(!isAddress(vault))
			throw new Error("property vault should be a blockchain addresss");

		const address = vault;
		const tok = getTokenDefinition(token);

		const [_, abi] = getContractWithABI("BracketVaultV2", chainId);
		const approved = await assertAllowance({ amount, spender: vault, token: tok, config, });

		if(!approved)
			throw new Error(`failed gaining approval for spending ${amount} ${tok.symbol} by ${vault}`);

		const [pclient, wclient] = await getViemClients({ chainId, config, });
		const bamount = typeof amount === "bigint" ? amount : parseUnits(amount.toString(), tok.decimals);

		const {
			request,
			result,
		} = await pclient.simulateContract({
			address,
			abi,
			functionName: "deposit",
			args: [
				bamount,
				wclient.account.address,
			],
			account: wclient.account,
		});

		const hash = await wclient.writeContract(request);

		if(isBlockHash(hash)) {
			await pclient.waitForTransactionReceipt({ hash, timeout })
			return hash;
		}

		throw new Error("expected result to be a bigint");

	}
	catch (err) {
		throw new Error(`@bracket/chainography/bracket-vault-v2 deposit failed - ${err.message}`);
	}
};
